import React, { useEffect, useMemo, useCallback, useRef } from "react";
import globals from "../../../globalVars";
import ListingPage from "../../../components/data/ListingPage";
import useStateModule from "../../../core/state/useStateModule";
import { useTranslation } from "react-i18next";
import GradeStatus from "../../../core/enums/GradeStatus";
import useImmerReducer from "../../../hooks/_shared/useImmerReducer";
import usePageChange from "../../../hooks/_shared/usePageChange";
import stockListingStateModule from "../../../core/state/stock/stockListingStateModule";
import FilterMenu from "../FilterMenu";
import { Select } from "../../../components/elements/form-elements";
import InputElement from "../../../components/elements/form-elements/InputElement";
import {
    StyledFilter,
    StyledArrow,
    StyledBack,
    StyledClose,
} from "./StyledSvg";

import {
    ScreenWrapper,
    ScreenHeader,
    ListTitle,
    FlexForm,
    FilterClear,
    FilterWrapper,
    HR,
} from "./StyledElements";

const actionTypes = {
    SET_PAGING: "SET_PAGING",
    SET_SORT: "SET_SORT",
    RESET_SORT: "RESET_SORT",
    SET_FILTERS: "SET_FILTERS",
    RESET_FILTERS: "RESET_FILTERS",
};

const filterReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_PAGING:
            state.pageSize = action.payload.pageSize;
            state.page = action.payload.pageIndex + 1;
            break;
        case actionTypes.SET_SORT:
            state.orderColumn = action.payload[0].id;
            state.descending = action.payload[0].desc;
            break;
        case actionTypes.RESET_SORT:
            state.orderColumn = "extractionDateTime";
            state.descending = true;
            break;
        case actionTypes.SET_FILTERS:
            state.filter.refrigerantGasTypeCode = action.payload.gasType;
            state.filter.quantityTo = action.payload.quantityTo;
            state.filter.quantityFrom = action.payload.quantityFrom;
            state.filter.presumedGrade = action.payload.presumedGrade;
            break;
        case actionTypes.RESET_FILTERS:
            delete state.filter.refrigerantGasTypeCode;
            delete state.filter.quantityTo;
            delete state.filter.quantityFrom;
            delete state.filter.presumedGrade;
            break;
        default:
            return state;
    }
};

const CompanyStockListing = (props) => {
    const {
        stockItems,
        isLoading,
        error,
        loadStockItems,
        loadGasTypes,
        gasTypesData,
    } = useStateModule(stockListingStateModule);

    const [filterState, dispatch] = useImmerReducer(filterReducer, {
        filter: {
            locationStatusName: "CompanyStock",
        },
        page: 1,
        pageSize: globals.maxListingItemsPerPage,
        orderColumn: "extractionDateTime",
        descending: true,
    });

    const { t } = useTranslation("screens");

    const filterMenuControl = {};

    useEffect(() => {
        loadStockItems(filterState);
    }, [loadStockItems, filterState]);

    useEffect(() => {
        loadGasTypes();
    }, [loadGasTypes]);

    const columns = useMemo(() => {
        const columns = [
            {
                Header: t("table:headers.gasType"),
                accessor: "refrigerantGasTypeCode",
            },
            {
                Header: t("table:headers.gasQuantity"),
                accessor: "quantity",
                className:"right",
                Cell: ({ cell: { value } }) => value.toFixed(2) + " kg",
            },
            {
                Header: t("stockListing.headers.quality"),
                accessor: "pressumedGradeName",
                className:"center"
            },
            {
                Header: "",
                accessor: "skuId",
                className: "right-arrow",
                Cell: ({ cell: { value } }) => <StyledArrow />,
                disableSortBy: true,
            },
        ];
        return columns;
    }, [filterState.filter, t]);

    const filterData = useRef({
        gasType: "",
        quantityFrom: "",
        quantityTo: "",
        presumedGrade:"",
    });

    const tempFilterData = useRef({});

    const hasFilters = () =>
        filterData.current.gasType ||
        filterData.current.quantityFrom ||
        filterData.current.quantityTo ||
        filterData.current.presumedGrade;

    const filterItems = () => {
        tempFilterData.current = {};
        return (
            <FlexForm autoComplete="off">
                <Select
                    className="filters big"
                    labelText={t("forms:gasType.text")}
                    name="refrigerantId"
                    id="refrigerantId"
                    wrapperVariant="flex-1"
                    defaultItem={{
                        key: "",
                        value: "",
                        label: "Please select a gas type",
                    }}
                    options={
                        gasTypesData.gasTypes &&
                        gasTypesData.gasTypes.map((gasType) => {
                            return {
                                key: gasType.code,
                                value: gasType.code,
                                label: gasType.code,
                            };
                        })
                    }
                    defaultValue={filterData.current.gasType}
                    handleChange={(evt) =>
                        (tempFilterData.current.gasType =
                            evt.currentTarget.value)
                    }
                />

                <InputElement
                    labelText={t("forms:quantity.from")}
                    id="quantityFrom"
                    name="quantityFrom"
                    wrapperVariant="flex-2"
                    type="number"
                    className="filters big left"
                    defaultValue={filterData.current.quantityFrom}
                    handleChange={(evt) => {
                        tempFilterData.current.quantityFrom =
                            evt.currentTarget.value;
                    }}
                />

                <InputElement
                    labelText={t("forms:quantity.to")}
                    id="quantityTo"
                    name="quantityTo"
                    wrapperVariant="flex-2"
                    type="number"
                    className="filters big right"
                    defaultValue={filterData.current.quantityTo}
                    handleChange={(evt) => {
                        tempFilterData.current.quantityTo =
                            evt.currentTarget.value;
                    }}
                />

                <Select
                    labelText={t("screens:stockListing.filters.quality")}
                    name="presumedGrade"
                    key="presumedGrade"
                    errors={[]}
                    touched={[]}
                    defaultItem={{
                        key: "all",
                        value: "",
                        label: t("screens:stockListing.filters.quality"),
                    }}
                    defaultValue={filterData.current.presumedGrade}
                    options={Object.keys(GradeStatus).map((key) => ({
                        key: key,
                        value: key,
                        label: GradeStatus[key],
                    }))}
                    handleChange={(evt) =>
                        (tempFilterData.current.presumedGrade =
                            evt.currentTarget.value)
                    }
                    className="filters big"
                    wrapperVariant="flex-1"
                />
            </FlexForm>
        );
    };

    const applyFilters = () => {
        Object.assign(filterData.current, tempFilterData.current);
        dispatch({
            type: actionTypes.SET_FILTERS,
            payload: filterData.current,
        });
    };

    const clearFilters = () => {
        filterData.current = {
            gasType: "",
            quantityFrom: "",
            quantityTo: "",
            presumedGrade:"",
        };
        dispatch({
            type: actionTypes.RESET_FILTERS,
        });
    };

    const clearFilter = (name) => {
        switch (name) {
            case "type":
                filterData.current.gasType = "";
                break;
            case "quantity":
                filterData.current.quantityFrom = "";
                filterData.current.quantityTo = "";
                break;
            case "quality":
                filterData.current.presumedGrade = "";
                break;
            default:
                break;
        }
        dispatch({
            type: actionTypes.SET_FILTERS,
            payload: filterData.current,
        });
    };

    const showFilter = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        filterMenuControl.setState({
            isOpen: true,
            hasFilters: hasFilters(),
            filterItems: filterItems(),
            applyFn: applyFilters,
            clearFn: clearFilters,
            text: hasFilters()
                ? t("screens:stockListing.filters.clearFilters")
                : t("screens:stockListing.filters.filterMyStock"),
        });
    };

    const onSelect = useCallback((row) => {
        props.onSelect && props.onSelect(row.original);
    }, []);

    const pageChange = usePageChange(dispatch, filterState);

    const sortChange = (sortBy) => {
        sortBy.length > 0
            ? dispatch({ type: actionTypes.SET_SORT, payload: sortBy })
            : dispatch({ type: actionTypes.RESET_SORT });
    };

    const currentSortBy = useMemo(() => {
        return [{ id: filterState.orderColumn, desc: filterState.descending }];
    }, [filterState.orderColumn, filterState.descending]);

    const curFilters = filterData.current;

    return (
        <ScreenWrapper>
            <FilterMenu control={filterMenuControl} />
            <ScreenHeader>
                <StyledBack className="disabled" />
                <FilterWrapper>
                    {curFilters.gasType && (
                        <FilterClear onClick={() => clearFilter("type")}>
                            {t("screens:stockListing.filters.type")}{" "}
                            <StyledClose />
                        </FilterClear>
                    )}

                    {(curFilters.quantityFrom || curFilters.quantityTo) && (
                        <FilterClear onClick={() => clearFilter("quantity")}>
                            {t("screens:stockListing.filters.quantity")}{" "}
                            <StyledClose />
                        </FilterClear>
                    )}

                    {(curFilters.presumedGrade ) && (
                        <FilterClear onClick={() => clearFilter("quality")}>
                            {t("screens:stockListing.filters.quality")}{" "}
                            <StyledClose />
                        </FilterClear>
                    )}

                    <StyledFilter onClick={showFilter} />
                </FilterWrapper>
            </ScreenHeader>
            <HR />
            <ListTitle>{t("screens:technician.cylinders")}</ListTitle>
            <ListingPage
                pwa
                noHeader
                columns={columns}
                tableState={{
                    data: stockItems.data,
                    loading: isLoading,
                    error: error,
                }}
                paging={{
                    enablePaging: true,
                    pageCount: stockItems.totalPages,
                    pageSize: stockItems.pageSize,
                    onPageChange: pageChange,
                    currentPageIndex: stockItems.currentPage - 1,
                }}
                sorting={{
                    enableSorting: true,
                    onSortByChange: sortChange,
                    currentSortBy: currentSortBy,
                }}
                onSelect={onSelect}
                noDataText={t("screens:stockListing.notification")}
            />
        </ScreenWrapper>
    );
};

export default CompanyStockListing;
